import * as React from 'react';
import { Divider, Grid, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { Tooltip } from '@swagup-com/components';
import { scrollBarAutohide } from '../../shared/styles/commonStyles';
import { formatColor } from './common';
import { changeColorLightness, isHexColor, isLightColor } from '../../shared/styles/utils';
import { getItemStatusText, itemStatusStyles, StatusChip, textOnHover } from '../../global/proofsCommon';

const useStyles = makeStyles({
  colorLabel: {
    fontFamily: 'Gilroy',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.57,
    color: '#787b80'
  },
  selectedColor: { color: '#0b1829' },
  colorContainer: {
    display: 'flex',
    gap: 8
  },
  colorName: {
    fontFamily: 'Gilroy',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.57,
    color: '#0b1829'
  },
  colorList: {
    ...scrollBarAutohide,
    maxHeight: 250
  },
  colorsSet: {
    paddingTop: 8,
    paddingBottom: 18,
    paddingLeft: 5,
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 20,
    rowGap: 8
  },
  showMoreButton: {
    fontSize: 14,
    height: 24
  },
  labelContainer: {
    display: 'flex',
    columnGap: 8
  },
  decorationMethod: {
    fontWeight: 600,
    fontSize: 20,
    lineHeight: 1.4,
    marginBottom: 26,
    color: '#000000'
  },
  statusContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  tooltip: {
    fontSize: 12,
    textAlign: 'justify',
    maxWidth: 320
  }
});

const useDotStyles = makeStyles({
  dotContainer: {
    height: 22, // add border size
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    columnGap: ({ selected }) => (selected ? 9 : 8),

    '&:hover': {
      cursor: 'pointer',
      '& p': {
        color: changeColorLightness('#3577d4')
      }
    }
  },
  dot: {
    height: ({ selected }) => (selected ? 15 : 16),
    width: ({ selected }) => (selected ? 15 : 16),
    backgroundColor: ({ backgroundColor }) => backgroundColor,
    borderRadius: '50%',
    display: 'inline-block',
    paddingRight: 8,
    verticalAlign: 'sub'
  },
  selectedDot: {
    boxShadow: ({ backgroundColor }) =>
      `0px 0px 0px 1px ${
        isLightColor(backgroundColor) ? '#ced1d6' : backgroundColor
      }, 0px 0px 0px 3px white, 0px 0px 0px 5px #3577d4`
  },
  selected: { color: '#3577d4' },
  border: {
    border: ({ backgroundColor }) => `1px solid ${isLightColor(backgroundColor) ? '#ced1d6' : backgroundColor}`
  },
  colorIcon: {
    width: 16,
    height: 16,
    borderRadius: '50%'
  },
  colorIconSelected: {
    boxShadow: ' 0px 0px 0px 3px white, 0px 0px 0px 5px #3577d4'
  },
  disabled: { pointerEvents: 'none' }
});

const ColorDot = ({ backgroundColor, colorName, selected, onClick, className, disabled, showColorName }) => {
  const classes = useDotStyles({ backgroundColor, selected });
  const isValidColor = isHexColor(backgroundColor);

  return (
    <div
      role="presentation"
      onClick={onClick}
      className={clsx(className, classes.dotContainer, disabled && classes.disabled)}
    >
      {isValidColor ? (
        <div className={clsx(classes.dot, selected ? classes.selectedDot : classes.border)} />
      ) : (
        <img
          src="/images/public/multicolor.png"
          alt="multicolor"
          className={clsx(selected && classes.colorIconSelected, classes.colorIcon)}
        />
      )}
      {showColorName && <p className={clsx(selected && classes.selected)}>{colorName}</p>}
    </div>
  );
};

const ColorPicker = ({ colors = [], onClick, selectedColor, disabled }) => {
  const classes = useStyles();

  return (
    <Grid item xs className={classes.colorList}>
      <div className={classes.colorsSet}>
        {colors.map(color => (
          <ColorDot
            key={color.id}
            selected={color.name === selectedColor.name}
            colorName={color.name}
            onClick={() => onClick({ name: color.name, hex: formatColor(color.hex_color) })}
            backgroundColor={formatColor(color.hex_color)}
            disabled={disabled}
            className={classes.colorName}
          />
        ))}
      </div>
    </Grid>
  );
};

const ColorSection = ({ itemStatus, colors, selectedColor, onClick, disabled }) => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.statusContainer}>
        <p className={classes.decorationMethod}>Decoration Methods</p>
        <Tooltip title={textOnHover[itemStatus]} arrow placement="top" classes={classes}>
          <StatusChip label={getItemStatusText(itemStatus)} status={itemStatusStyles[itemStatus]} />
        </Tooltip>
      </div>
      <div className={classes.labelContainer}>
        <p className={classes.colorLabel}>Selected color:</p>
        <ColorDot
          colorName={selectedColor.name}
          backgroundColor={selectedColor.hex}
          disabled
          showColorName
          className={classes.colorName}
        />
      </div>
      <ColorPicker colors={colors} selectedColor={selectedColor} onClick={onClick} disabled={disabled} />
      <Divider style={{ marginTop: 20 }} />
    </>
  );
};

export default ColorSection;
